import { place, type RoomContext, type RoomDecorator } from '../index';
import * as kit from '../../castle-kit';

/**
 * CHAPEL (Часовня) — the sacred, hushed heart of Castle Hearthhold: a place to pause, not to perform. The
 * HERO is the stone ALTAR at the north, chalice and candles catching the light, a heraldic STAINED-GLASS
 * window glowing rose behind it. Four PEWS face north toward it in two devotional rows; white LILIES flank
 * the altar (the only blooms allowed indoors — pure white, never garden green), votive banks pool a gentle
 * warm glow against the side walls, and a tall candelabra lifts the brightest beat onto the altar step.
 * Reverent, calm, soft rose — a quiet room you walk into and lower your voice.
 *
 * Composition is deliberately SPARE and mirror-symmetric (symmetry = reverence): one clear light
 * hierarchy — altar candles brightest → candelabra on the step → a single votive bank glowing low on
 * each side wall — with nothing doubled and the side walls left to breathe. Two banners frame the altar;
 * the rest is open floor for ducks to kneel.
 *
 * Geometry: zone (800,360) 380×400 → centre (990,560). Doors at N@x990, W@y560, E@y560, S@x990 — the
 * central cross + each door mouth is kept clear; set-dressing hugs the altar dais + the side walls.
 */
const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { art } = ctx;

  // ── Floor: a slim devotional runner drawing the eye up the nave to the altar (laid first, under all). ──
  place(ctx, kit.runnerRug(art, 96, 280), 990, 580);

  // ── The HERO: heraldic stained-glass window glowing behind the stone ALTAR at the north wall. ──
  place(ctx, kit.stainedGlass(art, 60, 120), 990, 400);
  place(ctx, kit.altar(art), 990, 440);

  // ── White lilies flanking the altar + a tall candelabra on the altar step (the brightest beat). ──
  place(ctx, kit.pottedLily(art), 920, 440);
  place(ctx, kit.pottedLily(art), 1060, 440);
  place(ctx, kit.candelabra(art), 990, 500);

  // ── The PEWS — two rows of two, every seat facing NORTH to the altar (sit anchors match exactly). ──
  for (const [px, py] of [
    [940, 620],
    [1040, 620],
    [940, 690],
    [1040, 690],
  ] as const) {
    place(ctx, kit.pew(art, 80), px, py);
  }

  // ── ONE votive bank against each side wall — the only secondary glow pools, left + right of the
  //    congregation; nothing else competes with the altar's candle focus. ──
  place(ctx, kit.votiveStand(art), 880, 560);
  place(ctx, kit.votiveStand(art), 1100, 560);

  // ── Two heraldic banners frame the altar high on the north wall — the sole wall-dressing, kept
  //    symmetric so the eye stays centred on the altar. ──
  place(ctx, kit.banner(art, undefined, 96), 852, 408);
  place(ctx, kit.banner(art, 0x8a2f2f, 96), 1128, 408);
};

export default decorate;
